import { string, array, object, number, boolean } from 'yup';

import {
  GENDER,
  PLACEMENT_SALARY_PER_TYPE,
  type TPlacement,
} from '@shared/types/placement';
import { getPlacementDataFromStorage } from '@features/placements/pages/create/helpers';
import { CURRENCY } from '@src/shared/types/currency';

export const positionFormNames = {
  position: 'secondStep.position',
  positionType: 'secondStep.position.type',
  positionTitle: 'secondStep.position.title',

  jobDescription: 'secondStep.jobDescription',
  availablePositions: 'secondStep.availablePositions',
  jobRequirements: 'secondStep.jobRequirements',
  requiredSkills: 'secondStep.requiredSkills',
  englishLevel: 'secondStep.englishLevel',
  genderPreference: 'secondStep.genderPreference',

  startDateRange: 'secondStep.startDateRange',
  startDateRangeFrom: 'secondStep.startDateRange.from',
  startDateRangeTo: 'secondStep.startDateRange.to',

  endDateRange: 'secondStep.endDateRange',
  endDateRangeFrom: 'secondStep.endDateRange.from',
  endDateRangeTo: 'secondStep.endDateRange.to',

  applicationDeadline: 'secondStep.applicationDeadline',
  averageHours: 'secondStep.averageHours',

  salary: 'secondStep.salary',
  salaryCurrency: 'secondStep.salary.currency',
  salaryAmount: 'secondStep.salary.amount',

  salaryPeriod: 'secondStep.salaryPeriod',
  includeTips: 'secondStep.includeTips',
} as const;

const existingPlacementData = (placementData?: TPlacement) => {
  if (!placementData) return undefined;

  return {
    position: {
      type: placementData.position_type,
      title: placementData.position_name,
    },
    jobDescription: placementData.position_description,
    availablePositions: String(placementData.number_of_positions),
    jobRequirements: placementData.position_requirements,
    requiredSkills: placementData.required_skills?.map((skill) => ({
      value: skill.name,
      label: skill.name,
      id: String(skill.id),
    })),
    englishLevel: placementData.language_level,
    genderPreference: placementData.gender,
    startDateRange: {
      from: placementData.earliest_start_date?.split?.('T')[0],
      to: placementData.latest_start_date?.split?.('T')[0],
    },
    endDateRange: {
      from: placementData.earliest_end_date?.split?.('T')[0],
      to: placementData.latest_end_date?.split?.('T')[0],
    },
    applicationDeadline:
      placementData.application_deadline?.split?.('T')[0] || '',
    averageHours: String(placementData.avg_hours_per_week),
    salary: {
      amount: placementData.salary,
      currency: placementData.salary_currency,
    },
    salaryPeriod: placementData.salary_per_type,
    includeTips: placementData.include_tips,
  };
};

export const createSecondStepInitialValues = (placementData?: TPlacement) => {
  const cachedPlacementData = getPlacementDataFromStorage() as {
    secondStep: ReturnType<typeof existingPlacementData>;
  };

  return {
    position: {
      type: '' as TPlacement['position_type'],
      title: '',
    },
    jobDescription: '',
    availablePositions: '',
    jobRequirements: '',
    requiredSkills: [] as { value: string; label: string; id: string }[],
    englishLevel: '' as TPlacement['language_level'],
    genderPreference: GENDER.female as TPlacement['gender'],
    startDateRange: {
      from: '',
      to: '',
    },
    endDateRange: {
      from: '',
      to: '',
    },
    applicationDeadline: '',
    averageHours: '',
    salary: {
      amount: '',
      currency: CURRENCY.usd,
    },
    salaryPeriod:
      PLACEMENT_SALARY_PER_TYPE.hourly as TPlacement['salary_per_type'],
    includeTips: false,

    ...cachedPlacementData?.secondStep,
    ...existingPlacementData(placementData),
  };
};

export const positionFormSchema = object().shape({
  secondStep: object().shape({
    position: object().shape({
      type: string().required('Position type is required'),
      title: string().required('Job position title is required'),
    }),
    jobDescription: string()
      .max(2000, 'Job description cannot exceed 2000 characters')
      .required('Job description is required'),
    availablePositions: number()
      .positive('Number must be positive')
      .integer('Number must be an integer')
      .required('Number of available positions is required'),

    // Job Requirements
    jobRequirements: string()
      .max(2000, 'Job requirements cannot exceed 2000 characters')
      .required('Job requirements are required'),
    requiredSkills: array()
      .min(1, 'At least one skill is required')
      .required('Required skills are required'),
    englishLevel: string().required('English level is required'),
    genderPreference: string().required('Gender preference is required'),

    // Date Information
    startDateRange: object().shape({
      from: string().required('Start date is required'),
      to: string().required('Start date is required'),
    }),
    endDateRange: object().shape({
      from: string().required('End date is required'),
      to: string().required('End date is required'),
    }),
    applicationDeadline: string().nullable(),
    // Salary/Hours Information
    averageHours: string().required('Average hours are required'),
    salary: object().shape({
      amount: number()
        .positive('Salary must be a positive number')
        .required('Salary amount is required'),
      currency: string().required('Currency is required'),
    }),
    salaryPeriod: string().required('Salary period is required'),
    includeTips: boolean(),
  }),
});

export type TPositionFormValues = ReturnType<
  typeof createSecondStepInitialValues
>;
